import type {
  U3129FindProjectItem,
  U3129HonorariumItem,
  U3129HonorariumTasksByProject,
  U3129TaskItem,
} from '../U3129Service';
import {
  buildHonorariumLinesFromProjectAndTasks,
  u3129HonorariumLineProjectSubprojectTaskKey,
  u3129HonorariumLineProjectSubprojectTaskKeyFromParts,
} from './u3129BuildHonorariumLinesFromTasks';

export const U3129_HONORARIUM_MULTI_PROJECT_MAX = 20;

export function u3129FindProjectChipSearchField(query: string): 'projetNumber' | 'projetName' {
  const trimmed = query.trim();
  if (/^\d/.test(trimmed)) {
    return 'projetNumber';
  }
  return 'projetName';
}

export function u3129ProjectIncludeSubProjetFlag(project: U3129FindProjectItem): boolean {
  const v = project.includeSubProjet;
  return v === true || (typeof v === 'string' && v === 'true');
}

/** Projet avec sous-projets : interdit sur la ligne multi ; bascule sous-projet si ligne vide. */
export function u3129ProjectRejectsMultiChipLine(
  project: U3129FindProjectItem,
  admIncludeSubProjectEnabled: boolean,
): boolean {
  return admIncludeSubProjectEnabled && u3129ProjectIncludeSubProjetFlag(project);
}

/** Libellé pastille dialogue honoraires multi-projets (numéro uniquement). */
export function u3129FindProjectChipLabel(project: U3129FindProjectItem): string {
  return project.projetNumber?.trim() || project.id;
}

export function u3129FindProjectChipTitle(project: U3129FindProjectItem): string {
  const num = project.projetNumber?.trim() || project.id;
  const name = project.projetName?.trim();
  return name ? `${num} — ${name}` : num;
}

export function u3129ResolveTaskItemForMultiProject(
  taskId: string,
  commonTasks: U3129TaskItem[],
  byProject: U3129HonorariumTasksByProject[],
  activeTasks: U3129TaskItem[],
  projectIdForNature?: string,
): U3129TaskItem | undefined {
  const id = taskId.trim();
  if (!id) {
    return undefined;
  }
  if (projectIdForNature) {
    const bucket = byProject.find((b) => b.projectId.trim() === projectIdForNature.trim());
    const fromProject = bucket?.tasks.find((t) => t.id === id);
    if (fromProject) {
      return fromProject;
    }
  }
  for (const bucket of byProject) {
    const hit = bucket.tasks.find((t) => t.id === id);
    if (hit) {
      return hit;
    }
  }
  const fromCommon = commonTasks.find((t) => t.id === id);
  if (fromCommon) {
    return fromCommon;
  }
  return activeTasks.find((t) => t.id === id);
}

/** Lignes honoraires : cartesian projet × tâche (sans sous-projet). */
export function u3129BuildHonorariumLinesForMultiProjects(
  projects: U3129FindProjectItem[],
  taskIds: string[],
  byProject: U3129HonorariumTasksByProject[],
  commonTasks: U3129TaskItem[],
  activeTasks: U3129TaskItem[],
  options: { admIncludeSubProjectEnabled: boolean },
): U3129HonorariumItem[] {
  const rows: U3129HonorariumItem[] = [];
  for (const project of projects) {
    for (const taskId of taskIds) {
      const task = u3129ResolveTaskItemForMultiProject(taskId, commonTasks, byProject, activeTasks, project.id);
      if (!task) {
        continue;
      }
      rows.push(
        ...buildHonorariumLinesFromProjectAndTasks(project, null, [task], {
          admIncludeSubProjectEnabled: options.admIncludeSubProjectEnabled,
        }),
      );
    }
  }
  return rows;
}

export function u3129HonorariumPairExistsOnSheet(
  projectId: string,
  taskId: string,
  existingHonoraria: U3129HonorariumItem[],
): boolean {
  const pid = projectId.trim();
  const tid = taskId.trim();
  const key = u3129HonorariumLineProjectSubprojectTaskKeyFromParts(pid, '', tid);
  return existingHonoraria.some((row) => u3129HonorariumLineProjectSubprojectTaskKey(row) === key);
}

/** Nombre de couples projet × tâche absents de la feuille. */
export function u3129CountNewHonorariumPairsMulti(
  projects: U3129FindProjectItem[],
  selectedTaskIds: Iterable<string>,
  existingHonoraria: U3129HonorariumItem[],
): number {
  let count = 0;
  for (const project of projects) {
    const pid = project.id.trim();
    for (const taskId of selectedTaskIds) {
      if (!u3129HonorariumPairExistsOnSheet(pid, taskId, existingHonoraria)) {
        count += 1;
      }
    }
  }
  return count;
}

/** Tâche désactivée si déjà sur la feuille pour chaque projet sélectionné. */
export function u3129MultiTaskRowFullyOnSheet(
  taskId: string,
  projects: U3129FindProjectItem[],
  existingHonoraria: U3129HonorariumItem[],
): boolean {
  if (projects.length === 0) {
    return true;
  }
  return projects.every((p) => u3129HonorariumPairExistsOnSheet(p.id, taskId, existingHonoraria));
}

/** Cache findTasksForProject par projet : assignées + pending depuis le catalogue. */
export function u3129BuildTasksForCacheByProjectId(
  byProject: U3129HonorariumTasksByProject[],
  pendingTaskIds: Set<string>,
  commonTasks: U3129TaskItem[],
  activeTasks: U3129TaskItem[],
): Record<string, U3129TaskItem[]> {
  const out: Record<string, U3129TaskItem[]> = {};
  for (const bucket of byProject) {
    const pid = bucket.projectId.trim();
    const map = new Map<string, U3129TaskItem>();
    for (const tk of bucket.tasks) {
      map.set(tk.id, tk);
    }
    for (const taskId of pendingTaskIds) {
      if (map.has(taskId)) {
        continue;
      }
      const resolved = u3129ResolveTaskItemForMultiProject(taskId, commonTasks, byProject, activeTasks, pid);
      if (resolved) {
        map.set(taskId, resolved);
      }
    }
    out[pid] = [...map.values()];
  }
  return out;
}
